import { useState, useEffect, useCallback } from "react";
import Cropper from "react-easy-crop";
import {
  Alert,
  Box,
  TextField,
  Grid,
  Typography,
  Button,
  IconButton,
  Paper,
  MenuItem,
  CircularProgress,
  Dialog,
  Modal,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
} from "@mui/material";
import { Add, Delete, Close } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/es";
import dayjs from "dayjs";
import PageHeader from "../../../components/PageHeader/PageHeader";
import { useTranslation } from "react-i18next";
import { getInvoice, createCustomInvoice, fetchFiscalTicket, lookupCompany } from "../../../services/SalesService";
import { normalizeRut, isValidRut } from "../../../utils/rut";
import { getCompanyLogos, fetchLogoPreviewUrl, uploadCompanyLogo, deleteCompanyLogo } from "../../../services/SettingsService";
import { readFile, getCroppedPng } from "../../../utils/ImageUtils";
import { getLocations } from "../../../services/LocationService";
import { getClosedMonths } from "../../../services/ElectronicBillingService";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import ConfiguredRutState from "../../../components/ElectronicBilling/ConfiguredRutState";
import useConfiguredBillingRuts from "../../../hooks/useConfiguredBillingRuts";
import InvoicePreviewModal from "./InvoicePreviewModal";
import { formatMoney, DEFAULT_CURRENCY } from '../../../utils/currency';
import { DEFAULT_IVA_RATE, IVA_RATE_OPTIONS, lineIvaRate, includedIva } from './invoiceIva';
import CurrencySelect from '../../../components/Currency/CurrencySelect';

// El logo ocupa como máximo 300x100: apaisado llena todo el ancho, cuadrado queda en 100x100.
const LOGO_MAX_WIDTH = 300;
const LOGO_MAX_HEIGHT = 100;
const LOGO_MAX_ASPECT = LOGO_MAX_WIDTH / LOGO_MAX_HEIGHT;

const parseDecimalInput = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (value === null || value === undefined) return 0;
  const normalized = String(value).trim().replace(/\s/g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const CreateInvoice = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [header, setHeader] = useState({
    invoiceType: "ticket",
    invoiceDate: new Date().toISOString().slice(0, 10),
    adenda: "",
    rut: "",
    razonSocial: "",
    direccionFiscal: "",
    locationId: "",
    currency: DEFAULT_CURRENCY,
  });

  const [lines, setLines] = useState([
    { description: "", quantity: "", price: "", discountType: "none", discountValue: "", ivaRate: DEFAULT_IVA_RATE },
  ]);

  const [saleSerie, setSaleSerie] = useState("");
  const [saleNumber, setSaleNumber] = useState("");
  const [loadedSale, setLoadedSale] = useState(null);
  const [loadingSale, setLoadingSale] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [closedMonths, setClosedMonths] = useState([]);
  // Logo solo para esta factura: "" = logo por defecto de la empresa.
  const [logoId, setLogoId] = useState("");
  const [logos, setLogos] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [newLogoName, setNewLogoName] = useState("");
  const [newLogoFile, setNewLogoFile] = useState(null);
  // Recorte del logo: el área puede ir de cuadrada (1:1) a apaisada (3:1), o sea hasta 300x100.
  const [logoSrc, setLogoSrc] = useState(null);
  const [logoCrop, setLogoCrop] = useState({ x: 0, y: 0 });
  const [logoZoom, setLogoZoom] = useState(1);
  const [logoAspect, setLogoAspect] = useState(LOGO_MAX_ASPECT);
  const [logoCropPixels, setLogoCropPixels] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  // Búsqueda del receptor por RUT, igual que en la venta: idle | found | not_found | manual.
  const [rutLookupStatus, setRutLookupStatus] = useState("idle");
  const [rutLookupLoading, setRutLookupLoading] = useState(false);

  // Al cerrar el visualizador se vuelve al listado de facturas enviadas.
  const handleClosePdf = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    navigate("/send_invoice");
  };
  const {
    ruts: configuredRuts,
    loading: loadingConfiguredRuts,
    error: configuredRutsError,
    reload: reloadConfiguredRuts
  } = useConfiguredBillingRuts({ allLocations: true });

  const isNotaCredito = header.invoiceType === "nc";
  // Configuración fiscal (RUT, razón social, domicilio) de la sucursal elegida: la misma que usa el PDF emitido.
  const issuerConfig = configuredRuts.find((entry) => String(entry.locationId) === String(header.locationId));
  // RUT con el que emite la sucursal elegida. El endpoint agrupa por RUT, así que si hay uno solo es el de todas.
  const issuerRut =
    configuredRuts.find((entry) => String(entry.locationId) === String(header.locationId))?.rut ||
    (new Set(configuredRuts.map((entry) => entry.rut)).size === 1 ? configuredRuts[0]?.rut : "");
  // La NC va en la moneda de la factura cargada; las facturas/tickets usan la elegida en el encabezado.
  const currency = isNotaCredito ? (loadedSale?.currency || DEFAULT_CURRENCY) : header.currency;

  const isDateInClosedMonth = (date) => {
    if (!date || !date.isValid()) return false;
    const y = date.year();
    const m = date.month() + 1;
    return closedMonths.some((entry) => entry.year === y && entry.month === m);
  };
  
  // Determinar si es consumidor final basado en el tipo de factura
  const isConsumerFinal = () => {
    if (isNotaCredito) {
      // Para NC, determinar basado en el tipo de la venta original
      return loadedSale?.type === "101"; // Ticket = consumidor final
    }
    // Para facturas normales: ticket = consumidor final, factura = NO consumidor final
    return header.invoiceType === "ticket";
  };

  useEffect(() => {
    getCompanyLogos().then(setLogos);
  }, []);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    fetchLogoPreviewUrl(logoId || null).then((url) => {
      objectUrl = url;
      if (!cancelled) setLogoPreview(url);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [logoId]);

  const handleDeleteLogo = async () => {
    const logo = logos.find((l) => l.id === logoId);
    if (!logo) return;
    if (!window.confirm(`¿Eliminar el logo "${logo.name}"? Las facturas que lo usaron volverán a mostrar el logo por defecto al regenerar el PDF.`)) return;
    try {
      await deleteCompanyLogo(logo.id);
      setLogos((prev) => prev.filter((l) => l.id !== logo.id));
      setLogoId("");
    } catch (e) {
      setError("No se pudo eliminar el logo");
    }
  };

  const resetLogoDialog = () => {
    setLogoDialogOpen(false);
    setNewLogoName("");
    setNewLogoFile(null);
    setLogoSrc(null);
    setLogoCrop({ x: 0, y: 0 });
    setLogoZoom(1);
    setLogoAspect(LOGO_MAX_ASPECT);
    setLogoCropPixels(null);
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0] || null;
    setNewLogoFile(file);
    setLogoSrc(file ? await readFile(file) : null);
    setLogoCrop({ x: 0, y: 0 });
    setLogoZoom(1);
  };

  const onLogoCropComplete = useCallback((_, pixels) => setLogoCropPixels(pixels), []);

  const handleUploadLogo = async () => {
    if (!newLogoName.trim() || !newLogoFile || !logoCropPixels) return;
    try {
      setUploadingLogo(true);
      // Se guarda a 2x de 300x100 para que se vea nítido en el PDF; nunca se agranda.
      const cropped = await getCroppedPng(logoSrc, logoCropPixels, {
        maxWidth: LOGO_MAX_WIDTH * 2,
        maxHeight: LOGO_MAX_HEIGHT * 2,
        fileName: `${newLogoFile.name.replace(/\.[^.]+$/, "")}.png`,
      });
      const created = await uploadCompanyLogo(newLogoName.trim(), cropped);
      setLogos((prev) => [...prev, created]);
      setLogoId(created.id);
      resetLogoDialog();
    } catch (e) {
      setError("No se pudo subir el logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const locationData = await getLocations();
        const filteredLocations = locationData.filter(location => !location.isPlant);
        setLocations(filteredLocations);
        // Siempre hay una sucursal emisora elegida: sin ella no se puede buscar el RUT del receptor.
        if (filteredLocations.length > 0) {
          setHeader((prev) => (prev.locationId ? prev : { ...prev, locationId: filteredLocations[0].id }));
        }
      } catch (error) {
        console.error('Error fetching locations:', error);
        setError('Error al cargar las sucursales');
      } finally {
        setLoadingLocations(false);
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    const fetchClosedMonths = async () => {
      try {
        const now = dayjs();
        const fromYear = now.year() - 2;
        const toYear = now.year() + 1;
        const list = await getClosedMonths({ fromYear, toYear });
        setClosedMonths(list);
      } catch (err) {
        console.error('Error fetching closed months:', err);
      }
    };
    fetchClosedMonths();
  }, []);

  const handleHeaderChange = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const handleRutChange = (value) => {
    // Cambiar el RUT invalida lo que se había encontrado.
    setHeader((prev) => ({ ...prev, rut: normalizeRut(value), razonSocial: "", direccionFiscal: "" }));
    setRutLookupStatus("idle");
  };

  const rutIsValid = isValidRut(header.rut);

  const handleRutLookup = async () => {
    if (!rutIsValid || rutLookupLoading) return;
    setRutLookupLoading(true);
    setError("");
    try {
      const result = await lookupCompany(normalizeRut(header.rut), header.locationId ? parseInt(header.locationId) : undefined);
      if (result?.found) {
        setHeader((prev) => ({
          ...prev,
          rut: result.rut || prev.rut,
          razonSocial: result.name || "",
          direccionFiscal: result.fiscalAddress || "",
        }));
        setRutLookupStatus("found");
      } else {
        setHeader((prev) => ({ ...prev, razonSocial: "", direccionFiscal: "" }));
        setRutLookupStatus("not_found");
      }
    } catch (err) {
      setError("No se pudo buscar el RUT");
    } finally {
      setRutLookupLoading(false);
    }
  };

  const handleLineChange = (index, field, value) => {
    const updated = [...lines];
    updated[index][field] = value;
    setLines(updated);
  };

  const addLine = () =>
    setLines([...lines, { description: "", quantity: "", price: "", discountType: "none", discountValue: "", ivaRate: DEFAULT_IVA_RATE }]);

  const removeLine = (index) => setLines(lines.filter((_, i) => i !== index));

  const calculateLineTotal = (line) => {
    const quantity = parseDecimalInput(line.quantity);
    const price = parseDecimalInput(line.price);
    const discountValue = parseDecimalInput(line.discountValue);
    const base = quantity * price;
    if (line.discountType === "amount") return Math.max(base - discountValue, 0);
    if (line.discountType === "percent") return base - base * (discountValue / 100);
    return base;
  };

  const items = isNotaCredito ? loadedSale?.items || [] : lines;
  const total = items.reduce((acc, line) => acc + calculateLineTotal(line), 0);
  // El IVA ya viene incluido en el precio: cada línea aporta según su propia tasa.
  const iva = includedIva(items, calculateLineTotal);
  const subtotal = total - iva;

  // Función para validar si el formulario está completo
  const isFormValid = () => {
    // Para Notas de Crédito, solo validar que haya una venta cargada
    if (isNotaCredito) {
      return loadedSale && loadedSale.items && loadedSale.items.length > 0;
    }

    // Para facturas normales, validar todos los campos necesarios
    const hasLocation = !!header.locationId;
    const hasValidItems = lines.length > 0 && lines.every(line => 
      line.description.trim() !== "" && 
      line.quantity && 
      parseDecimalInput(line.quantity) > 0 && 
      line.price && 
      parseDecimalInput(line.price) > 0
    );

    // Si no es consumidor final, validar campos fiscales
    const fiscalFieldsValid = isConsumerFinal() || (
      rutIsValid &&
      (rutLookupStatus === "found" || rutLookupStatus === "manual") &&
      header.rut.trim() !== "" && 
      header.razonSocial.trim() !== "" && 
      header.direccionFiscal.trim() !== ""
    );

    return hasLocation && hasValidItems && fiscalFieldsValid;
  };

  const handleSubmit = async () => {
    // Validar que se haya seleccionado una sucursal (solo para facturas normales, no NC)
    if (!isNotaCredito && !header.locationId) {
      setError("Debe seleccionar una sucursal");
      return;
    }

    let typeCode;
    if (header.invoiceType === "ticket") typeCode = "101";
    else if (header.invoiceType === "factura") typeCode = "111";
    else if (header.invoiceType === "nc") {
      if (loadedSale?.type === "101") typeCode = "102";
      else if (loadedSale?.type === "111") typeCode = "112";
      else {
        setError("No se puede determinar el tipo de NC");
        return;
      }
    }

    const invoiceData = {
      invoiceType: typeCode,
      isCreditNote: header.invoiceType === "nc",
      ...(isNotaCredito
        ? { 
            // La NC siempre referencia el documento cargado: el backend usa
            // estos datos para impedir que se acredite otra factura.
            saleId: loadedSale.id,
            originalSerie: loadedSale.serie,
            originalNumber: loadedSale.number,
            originalDate: loadedSale.date,
            currency,
            items: items.map((item) => ({
              description: item.description,
              quantity: parseDecimalInput(item.quantity),
              price: parseDecimalInput(item.price),
              discountType: item.discountType,
              discountValue: parseDecimalInput(item.discountValue),
              ivaRate: lineIvaRate(item),
            }))
          }
        : {
            locationId: parseInt(header.locationId), // Solo para facturas normales (NO NC)
            logoId: logoId || null,
            currency,
            items: lines.map((line) => ({
              description: line.description,
              quantity: parseDecimalInput(line.quantity),
              price: parseDecimalInput(line.price),
              discountType: line.discountType,
              discountValue: parseDecimalInput(line.discountValue),
              ivaRate: lineIvaRate(line),
            })),
          }),
      consumerFinal: isConsumerFinal(),
      invoiceDate: header.invoiceDate,
      adenda: header.adenda || null,
      rut: header.rut || null,
      razonSocial: header.razonSocial || null,
      direccionFiscal: header.direccionFiscal || null,
      subtotal: parseFloat(subtotal.toFixed(2)),
      iva: parseFloat(iva.toFixed(2)),
      total: parseFloat(total.toFixed(2)),
    };

    try {
      setSubmitting(true);
      var data = await createCustomInvoice(invoiceData);
      if (data) {
        // Abre el PDF recién emitido; si no se puede obtener, va directo al listado.
        const externalId = data.externalDocumentId;
        const pdfBlob = externalId ? await fetchFiscalTicket(externalId, header.rut || undefined) : null;
        if (pdfBlob) {
          setPdfUrl(URL.createObjectURL(pdfBlob));
        } else {
          navigate("/send_invoice");
        }
      }
      setSubmitting(false);
    } catch (err) {
      console.error("Error generando factura:", err);
    }
  };

  const handleLoadSale = async () => {
    setLoadingSale(true);
    setError("");
    try {
      console.log('🔍 Cargando factura para Nota de Crédito - Serie:', saleSerie, 'Número:', saleNumber);
      const data = await getInvoice(saleSerie, saleNumber);
      if (!data) {
        console.log('❌ Factura no encontrada para Serie:', saleSerie, 'Número:', saleNumber);
        setError("Factura no encontrada");
        setLoadedSale(null);
      } else {
        console.log('✅ Factura cargada exitosamente:', data);
        setLoadedSale({
          id: data.id,
          type: data.type,
          serie: data.serie,
          number: data.number,
          date: data.date,
          currency: data.currency || DEFAULT_CURRENCY,
          exchangeRate: data.exchangeRate ?? null,
          items: data.items.map((item) => ({
            description: item.description,
            quantity: item.quantity,
            price: item.unitPrice,
            discountType:
              item.discountPercent > 0 ? "percent" :
              item.discountAmount > 0 ? "amount" : "none",
            discountValue:
              item.discountPercent > 0 ? item.discountPercent :
              item.discountAmount > 0 ? item.discountAmount : "",
            ivaRate: item.ivaRate ?? DEFAULT_IVA_RATE,
          })),
        });
      }
    } catch (err) {
      console.error('❌ Error al cargar factura para Nota de Crédito:', {
        serie: saleSerie,
        number: saleNumber,
        error: err.message || err
      });
      setError("Factura no encontrada");
      setLoadedSale(null);
    } finally {
      setLoadingSale(false);
    }
  };

  const handleRemoveItemFromNC = (index) => {
    if (!loadedSale) return;
    const updated = loadedSale.items.filter((_, i) => i !== index);
    setLoadedSale({ ...loadedSale, items: updated });
  };

  return (
    <Box>
      {loadingConfiguredRuts ? <ConfiguredRutState loading /> : configuredRutsError || configuredRuts.length === 0 ? (
        <ConfiguredRutState error={configuredRutsError} onRetry={reloadConfiguredRuts} />
      ) : (
        <Box p={3}>
          <PageHeader title={t("create_invoice")} subtitle={t("create_invoice_desc")} />

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {!isNotaCredito && (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Logo
          </Typography>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Logo de esta factura"
                  value={logoId}
                  onChange={(e) => setLogoId(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="">Logo por defecto</MenuItem>
                  {logos.map((logo) => (
                    <MenuItem key={logo.id} value={logo.id}>{logo.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button variant="outlined" startIcon={<Add />} onClick={() => setLogoDialogOpen(true)} sx={{ height: "56px !important", minHeight: "56px !important" }}>
                    Agregar logo
                  </Button>
                  {logoId !== "" && (
                    <Button color="error" startIcon={<Delete />} onClick={handleDeleteLogo} sx={{ height: "56px !important", minHeight: "56px !important" }}>
                      Eliminar
                    </Button>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    height: 72,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {logoPreview ? (
                    <Box component="img" src={logoPreview} alt="Logo" sx={{ maxWidth: 140, maxHeight: 60, objectFit: "contain" }} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">Sin logo</Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Solo aplica a esta factura; no cambia el logo de la empresa.
                </Typography>
              </Grid>
            </Grid>
          </Paper>
          <Dialog open={logoDialogOpen} onClose={resetLogoDialog} fullWidth maxWidth="sm">
            <DialogTitle>Agregar logo</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                margin="dense"
                label="Nombre"
                value={newLogoName}
                onChange={(e) => setNewLogoName(e.target.value)}
              />
              <Button component="label" sx={{ mt: 1 }}>
                {newLogoFile ? newLogoFile.name : "Elegir imagen (PNG/JPG)"}
                <input
                  hidden
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={handleLogoFileChange}
                />
              </Button>
              {logoSrc && (
                <>
                  <Box sx={{ position: "relative", width: "100%", height: 240, mt: 2, bgcolor: "grey.100" }}>
                    <Cropper
                      image={logoSrc}
                      crop={logoCrop}
                      zoom={logoZoom}
                      aspect={logoAspect}
                      onCropChange={setLogoCrop}
                      onZoomChange={setLogoZoom}
                      onCropComplete={onLogoCropComplete}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Forma del recorte: de cuadrado ({LOGO_MAX_HEIGHT}x{LOGO_MAX_HEIGHT}) a apaisado ({LOGO_MAX_WIDTH}x{LOGO_MAX_HEIGHT})
                  </Typography>
                  <Slider min={1} max={LOGO_MAX_ASPECT} step={0.05} value={logoAspect} onChange={(_, v) => setLogoAspect(v)} />
                  <Typography variant="caption" color="text.secondary">Zoom</Typography>
                  <Slider min={1} max={3} step={0.05} value={logoZoom} onChange={(_, v) => setLogoZoom(v)} />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={resetLogoDialog}>Cancelar</Button>
              <LoadingButton
                loading={uploadingLogo}
                disabled={!newLogoName.trim() || !newLogoFile || !logoCropPixels}
                onClick={handleUploadLogo}
              >
                Guardar
              </LoadingButton>
            </DialogActions>
          </Dialog>
        </>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>
        Comprobante
      </Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Tipo de factura"
              fullWidth
              value={header.invoiceType}
              onChange={(e) => handleHeaderChange("invoiceType", e.target.value)}
            >
              <MenuItem value="ticket">Ticket</MenuItem>
              <MenuItem value="factura">Factura</MenuItem>
              <MenuItem value="nc">Nota de Crédito</MenuItem>
            </TextField>
          </Grid>

          {!isNotaCredito && (
            <>
              <Grid item xs={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                  <DatePicker
                    label={t("date") || "Fecha"}
                    value={dayjs(header.invoiceDate)}
                    onChange={(date) => {
                      if (date && date.isValid()) {
                        handleHeaderChange("invoiceDate", date.format("YYYY-MM-DD"));
                      }
                    }}
                    shouldDisableDate={isDateInClosedMonth}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Moneda - solo con el plugin multimoneda activo (el selector se oculta con una sola moneda) */}
              <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                <CurrencySelect
                  size="medium"
                  value={header.currency}
                  onChange={(value) => handleHeaderChange("currency", value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Adenda"
                  fullWidth
                  multiline
                  rows={2}
                  value={header.adenda}
                  onChange={(e) => handleHeaderChange("adenda", e.target.value)}
                />
              </Grid>
            </>
          )}

          {isNotaCredito && (
            <>
              <Grid item xs={12} md={2}>
                <TextField
                  label="Serie"
                  fullWidth
                  value={saleSerie}
                  onChange={(e) => {
                    setSaleSerie(e.target.value);
                    setLoadedSale(null); // limpiar datos previos
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="Número"
                  fullWidth
                  value={saleNumber}
                  onChange={(e) => {
                    setSaleNumber(e.target.value);
                    setLoadedSale(null); // limpiar datos previos
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{ height: "56px !important", minHeight: "56px !important" }}
                  onClick={handleLoadSale}
                  disabled={!saleSerie || !saleNumber || loadingSale}
                >
                  {loadingSale ? <CircularProgress size={20} /> : "Cargar Venta"}
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Emisor / Receptor: una NC toma ambos de la factura original, así que no se piden */}
      {!isNotaCredito && (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Emisor
          </Typography>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Sucursal emisora"
                  required
                  fullWidth
                  value={header.locationId}
                  onChange={(e) => handleHeaderChange("locationId", e.target.value)}
                  disabled={loadingLocations}
                  helperText={loadingLocations ? "Cargando sucursales..." : "La sucursal define con qué RUT y numeración se emite."}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Paper>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Receptor
          </Typography>
          <Paper sx={{ p: 2, mb: 3 }}>
            {isConsumerFinal() ? (
              <Typography variant="body2" color="text.secondary">
                Consumidor final: un ticket no lleva datos del receptor. Elegí "Factura" para ingresar RUT, razón social y dirección.
              </Typography>
            ) : (
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                    <TextField
                      label="RUT"
                      fullWidth
                      value={header.rut}
                      onChange={(e) => handleRutChange(e.target.value)}
                      onBlur={() => { if (rutIsValid && rutLookupStatus === "idle") handleRutLookup(); }}
                      error={!!header.rut && !rutIsValid}
                      helperText={header.rut && !rutIsValid ? "Ingresá un RUT válido" : " "}
                    />
                    <Button
                      variant="contained"
                      onClick={handleRutLookup}
                      disabled={!rutIsValid || rutLookupLoading}
                      sx={{ minWidth: 120, height: "56px !important", minHeight: "56px !important" }}
                    >
                      {rutLookupLoading ? "Buscando..." : "Buscar"}
                    </Button>
                  </Box>
                </Grid>

                {rutLookupStatus === "found" && (
                  <Grid item xs={12} md={6}>
                    <Alert severity="success" sx={{ height: 56, alignItems: "center" }}>Datos fiscales encontrados</Alert>
                  </Grid>
                )}

                {rutLookupStatus === "not_found" && (
                  <Grid item xs={12}>
                    <Alert
                      severity="warning"
                      action={
                        <Button color="inherit" size="small" onClick={() => setRutLookupStatus("manual")}>
                          Registrar de todas formas
                        </Button>
                      }
                    >
                      No se encontró el RUT. Revisá el número antes de registrarlo manualmente.
                    </Alert>
                  </Grid>
                )}

                {(rutLookupStatus === "found" || rutLookupStatus === "manual") && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Razón Social"
                        fullWidth
                        value={header.razonSocial}
                        onChange={(e) => handleHeaderChange("razonSocial", e.target.value)}
                        disabled={rutLookupStatus === "found"}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Dirección Fiscal"
                        fullWidth
                        value={header.direccionFiscal}
                        onChange={(e) => handleHeaderChange("direccionFiscal", e.target.value)}
                        disabled={rutLookupStatus === "found"}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            )}
          </Paper>
        </>
      )}

      {/* Items o NC */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        {isNotaCredito ? "Nota de Crédito" : "Items"}
      </Typography>

      {isNotaCredito ? (
        loadedSale && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">Nota de Crédito</Typography>
              <Typography variant="body2">
                Tipo de documento: {loadedSale.type === "101" ? "Ticket" : "Factura"}
              </Typography>
              <Typography variant="body2">
                Serie y número: {loadedSale.serie}{loadedSale.number ? `-${loadedSale.number}` : ""}
              </Typography>
              <Typography variant="body2">
                Fecha de emisión: {new Date(loadedSale.date).toLocaleDateString()}
              </Typography>
            </Box>
            {loadedSale.items.map((item, idx) => (
              <Paper key={idx} sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={4}>
                    <TextField label="Descripción" fullWidth value={item.description} disabled />
                  </Grid>
                  <Grid item xs={1.5}>
                    <TextField label="Cant." fullWidth value={item.quantity} disabled />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField label="Precio (IVA inc.)" fullWidth value={item.price} disabled />
                  </Grid>
                  <Grid item xs={1.5}>
                    <TextField
                      select
                      label="Tipo desc."
                      fullWidth
                      value={item.discountType}
                      disabled
                    >
                      <MenuItem value="none">N/A</MenuItem>
                      <MenuItem value="amount">Monto</MenuItem>
                      <MenuItem value="percent">%</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={1.5}>
                    <TextField
                      label="Valor desc."
                      fullWidth
                      value={item.discountValue}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <Typography align="right">{formatMoney(calculateLineTotal(item), currency)}</Typography>
                  </Grid>
                  <Grid item xs={0.5}>
                    <IconButton color="error" onClick={() => handleRemoveItemFromNC(idx)}>
                      <Delete />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Paper>
        )
      ) : (
        <>
          {lines.map((line, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={2.5}>
                  <TextField
                    label="Descripción"
                    fullWidth
                    value={line.description}
                    onChange={(e) => handleLineChange(index, "description", e.target.value)}
                  />
                </Grid>
                <Grid item xs={1.5}>
                  <TextField
                    label="Cant."
                    type="text"
                    inputMode="decimal"
                    fullWidth
                    value={line.quantity}
                    onChange={(e) => handleLineChange(index, "quantity", e.target.value)}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="Precio (IVA inc.)"
                    type="text"
                    inputMode="decimal"
                    fullWidth
                    value={line.price}
                    onChange={(e) => handleLineChange(index, "price", e.target.value)}
                  />
                </Grid>
                <Grid item xs={1.5}>
                  <TextField
                    select
                    label="IVA"
                    fullWidth
                    value={lineIvaRate(line)}
                    onChange={(e) => handleLineChange(index, "ivaRate", Number(e.target.value))}
                    inputProps={{ "data-testid": `invoice-line-iva-${index}` }}
                  >
                    {IVA_RATE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={1.5}>
                  <TextField
                    select
                    label="Tipo desc."
                    fullWidth
                    value={line.discountType}
                    onChange={(e) => handleLineChange(index, "discountType", e.target.value)}
                  >
                    <MenuItem value="none">N/A</MenuItem>
                    <MenuItem value="amount">Monto</MenuItem>
                    <MenuItem value="percent">%</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={1.5}>
                  <TextField
                    label="Valor desc."
                    type="text"
                    inputMode="decimal"
                    fullWidth
                    disabled={line.discountType === "none"}
                    value={line.discountValue}
                    onChange={(e) => handleLineChange(index, "discountValue", e.target.value)}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Typography align="right">{formatMoney(calculateLineTotal(line), currency)}</Typography>
                </Grid>
                <Grid item xs={0.5}>
                  <IconButton color="error" onClick={() => removeLine(index)}>
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Button startIcon={<Add />} onClick={addLine}>
            Agregar línea
          </Button>
        </>
      )}

      {/* Totales */}
      <Box mt={3} display="flex" justifyContent="flex-end">
        <Box textAlign="right">
          <Typography variant="body1">Subtotal: {formatMoney(subtotal, currency)}</Typography>
          <Typography variant="body1">IVA: {formatMoney(iva, currency)}</Typography>
          <Typography variant="h6">Total: {formatMoney(total, currency)}</Typography>
        </Box>
      </Box>

      <Box mt={2} mb={5} display="flex" justifyContent="flex-end" gap={1}>
        <Button variant="outlined" onClick={() => setPreviewOpen(true)} disabled={isNotaCredito && !loadedSale}>
          Vista previa
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          loading={submitting}
          disabled={!isFormValid()}
        >
          {isNotaCredito ? "Generar NC" : "Generar Factura"}
        </LoadingButton>
      </Box>
        </Box>
      )}
      <InvoicePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        documentLabel={
          isNotaCredito
            ? `e-Nota de Crédito`
            : header.invoiceType === "factura" ? "e-Factura" : "e-Ticket"
        }
        isCreditNote={isNotaCredito}
        logoUrl={logoPreview}
        issuerName={issuerConfig?.razonSocial}
        issuerAddress={issuerConfig?.companyAddress}
        issuerRut={issuerRut}
        date={isNotaCredito ? null : header.invoiceDate}
        consumerFinal={isConsumerFinal()}
        rut={header.rut}
        razonSocial={header.razonSocial}
        adenda={header.adenda}
        reference={isNotaCredito && loadedSale ? `${loadedSale.serie}${loadedSale.number ? `-${loadedSale.number}` : ""}` : null}
        lines={items.map((line) => ({
          description: line.description,
          quantity: parseDecimalInput(line.quantity),
          price: parseDecimalInput(line.price),
          discountType: line.discountType,
          discountValue: parseDecimalInput(line.discountValue),
          total: calculateLineTotal(line),
        }))}
        currency={currency}
        subtotal={subtotal}
        iva={iva}
        total={total}
      />
      {/* Visualizador del PDF de la factura recién generada */}
      <Modal
        open={!!pdfUrl}
        onClose={handleClosePdf}
        aria-labelledby="pdf-modal-title"
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            width: "90%",
            height: "90%",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography id="pdf-modal-title" variant="h6" component="h2">
              Visualizador de PDF
            </Typography>
            <IconButton onClick={handleClosePdf} size="small">
              <Close />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {pdfUrl && (
              <iframe
                src={pdfUrl}
                width="100%"
                height="100%"
                style={{ border: "none", borderRadius: "4px" }}
                title="PDF Viewer"
              />
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default CreateInvoice;
